export type RequirementMode = 'off' | 'optional' | 'required'

export const OPTIONAL_FIELDS_KEY = 'optional_fields'

export const LICENCE_REQUIREMENTS_KEY = 'licence_requirements'

export const LIABILITY_WAIVER_KEY = 'liability_waiver'

export const DRIVERS_LICENSE_KEY = 'drivers_license'

export const REQUIRED_LICENSE_TYPES_KEY = 'required_license_types'

export const ALWAYS_REQUIRED_REQUIREMENT_KEYS: string[] = [
  'full_name',
  'email',
  'phone_number',
]

export const CONFIGURABLE_REQUIREMENT_KEYS: string[] = [
  'date_of_birth',
  'address',
  'emergency_contact_1',
  'emergency_contact_2',
  'vehicle_info',
  LICENCE_REQUIREMENTS_KEY,
  DRIVERS_LICENSE_KEY,
  LIABILITY_WAIVER_KEY,
]

export const LEGACY_SKIPPABLE_REQUIREMENT_KEYS: string[] = [
  'date_of_birth',
  'address',
  'emergency_contact_1',
  'emergency_contact_2',
  'vehicle_info',
  LICENCE_REQUIREMENTS_KEY,
]

export function requiredLicenseTypesOf(
  requirements: Record<string, unknown> | undefined,
): string[] {
  const raw = requirements?.[REQUIRED_LICENSE_TYPES_KEY]
  if (!Array.isArray(raw)) return []
  return raw.filter((type): type is string => typeof type === 'string')
}

export function optionalFieldsOf(
  requirements: Record<string, unknown> | undefined,
): string[] {
  const raw = requirements?.[OPTIONAL_FIELDS_KEY]
  if (!Array.isArray(raw)) return [...LEGACY_SKIPPABLE_REQUIREMENT_KEYS]
  return raw.filter((field): field is string => typeof field === 'string')
}

function collectsKey(
  requirements: Record<string, unknown> | undefined,
  key: string,
): boolean {
  if (key === LICENCE_REQUIREMENTS_KEY) {
    return (
      requirements?.[key] === true || requiredLicenseTypesOf(requirements).length > 0
    )
  }
  return requirements?.[key] === true
}

export function requirementModeOf(
  requirements: Record<string, unknown> | undefined,
  key: string,
): RequirementMode {
  if (ALWAYS_REQUIRED_REQUIREMENT_KEYS.includes(key)) return 'required'
  if (!collectsKey(requirements, key)) return 'off'
  return optionalFieldsOf(requirements).includes(key) ? 'optional' : 'required'
}

export function requirementModesOf(
  requirements: Record<string, unknown> | undefined,
): Record<string, RequirementMode> {
  const modes: Record<string, RequirementMode> = {}
  for (const key of ALWAYS_REQUIRED_REQUIREMENT_KEYS) {
    modes[key] = 'required'
  }
  for (const key of CONFIGURABLE_REQUIREMENT_KEYS) {
    modes[key] = requirementModeOf(requirements, key)
  }
  return modes
}

export function requirementsFromModes(
  modes: Record<string, RequirementMode>,
): { flags: Record<string, boolean>; optionalFields: string[] } {
  const flags: Record<string, boolean> = {}
  const optionalFields: string[] = []

  for (const key of ALWAYS_REQUIRED_REQUIREMENT_KEYS) {
    flags[key] = true
  }

  for (const key of CONFIGURABLE_REQUIREMENT_KEYS) {
    const mode = modes[key] ?? 'off'
    flags[key] = mode !== 'off'
    if (mode === 'optional') optionalFields.push(key)
  }

  return { flags, optionalFields }
}
